import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileOutlined";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { ComposerAttachment } from "ai-assistant/attachments/_views/use-assistant-attachments";

interface AssistantAttachmentStripProps {
  items: readonly ComposerAttachment[];
  notice?: string;
  sendBlockedReason?: string;
  /** While a message is being sent, its files are not to be changed under it. */
  disabled?: boolean;
  onRetry: (key: string) => void;
  onRemove: (key: string) => void;
}

const Strip = styled("div")(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 1.5, 0),
}));

const Rows = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.25, 0.5, 0.25, 1),
  fontSize: "0.85rem",
}));

const FileName = styled("span")({
  flexGrow: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const Issue = styled("div")(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "0.8rem",
  padding: theme.spacing(0, 1, 0.5),
}));

const Note = styled("div")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.8rem",
  padding: theme.spacing(0.5, 0),
}));

function statusText(item: ComposerAttachment): string {
  if (item.status === "uploading") return "Uploading";
  if (item.status === "failed") return "Did not upload";
  return "Attached";
}

/**
 * The files about to go with the next assistant message, above the composer (#6504).
 *
 * Each row says where its upload stands. A failed one says why, in the server's words where it gave
 * them, and offers Retry; any row can be removed.
 */
export default function AssistantAttachmentStrip({
  items,
  notice,
  sendBlockedReason,
  disabled,
  onRetry,
  onRemove,
}: Readonly<AssistantAttachmentStripProps>) {
  if (items.length === 0 && !notice) return null;

  return (
    <Strip>
      {items.length > 0 && (
        <Rows aria-label="Attached files">
          {items.map((item) => (
            <li key={item.key}>
              <Row aria-busy={item.status === "uploading"}>
                {item.status === "failed"
                  ? <ErrorOutlineIcon fontSize="small" color="error" />
                  : <InsertDriveFileIcon fontSize="small" color="action" />}
                <FileName title={item.file.name}>{item.file.name}</FileName>
                <Note as="span">{statusText(item)}</Note>
                {item.status === "uploading" && (
                  <CircularProgress size={16} aria-label={`Uploading ${item.file.name}`} />
                )}
                {item.status === "failed" && (
                  <Button size="small" disabled={disabled} onClick={() => onRetry(item.key)}
                    aria-label={`Retry ${item.file.name}`}>
                    Retry
                  </Button>
                )}
                <IconButton size="small" disabled={disabled} onClick={() => onRemove(item.key)}
                  aria-label={`Remove ${item.file.name}`}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Row>
              {item.status === "failed" && item.issue && <Issue role="alert">{item.issue}</Issue>}
            </li>
          ))}
        </Rows>
      )}
      {notice && <Issue role="alert" sx={{ px: 0, pt: 0.5 }}>{notice}</Issue>}
      {sendBlockedReason && <Note role="status">{sendBlockedReason}</Note>}
    </Strip>
  );
}
